/**
 * terrain.api.ts — Módulos, válvulas y proyectos (F1).
 *
 * Espejo de `aws-serverless/src/models/terrain.model.ts`. El cliente inyecta el
 * tenant en la URL (`client.ts`), por eso las rutas van sin él.
 */

import { apiClient } from './client';
import type { GeoJSONPolygon } from '../types/api.types';

export type ModuleStatus = 'PREPARACION' | 'NORMAL' | 'ATENCION' | 'CRITICO' | 'COSECHANDO';
export type ProjectStatus = 'PLANNED' | 'ACTIVE' | 'CLOSED';

export interface FarmModule {
  moduleId: string;
  tenantId: string;
  ranchoId: string;
  loteId: string;
  moduleCode: string;
  name?: string;
  valveIds: string[];
  boundary?: GeoJSONPolygon;
  /** Punto central de la geocerca, calculado al guardarla. */
  latitude?: number;
  longitude?: number;
  areaHa?: number;
  /** @deprecated usar cropId — ver Crop/fetchCrops */
  cropName?: string;
  cropId?: string;
  projectId?: string;
  /** Zona padre (un solo nivel de anidación). */
  parentModuleId?: string;
  plantingDate?: string;
  /** Estado del último ciclo (D16): CLOSED = el mapa lo pinta atenuado. */
  cropCycleStatus?: 'OPEN' | 'CLOSED';
  cropCycleLabel?: string;
  /** Color elegido por el usuario para pintar la zona en el mapa (#rrggbb). */
  displayColor?: string;
  /** Lugar válido para pasar lista (entrada, gerencia, campo...): la geocerca de asistencia usa estas zonas. */
  attendanceZone?: boolean;
  status: ModuleStatus;
  statusReason?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface Crop {
  cropId: string;
  tenantId: string;
  name: string;
  colorHex: string;
  /** Tipo agronómico opcional, para los cálculos de fenología. */
  cropType?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ModuleCropHistoryEntry {
  moduleId: string;
  startDate: string;
  tenantId: string;
  cropId: string;
  endDate: string | null;
  reason: string;
  createdAt: string;
}

export interface ModuleNdviResult {
  moduleId: string;
  imageUrl: string | null;
  ndviStats: { min: number; max: number; mean: number } | null;
  date: string;
  cloudCoverage: number | null;
  /** Fecha de la escena satelital usada (backend >= 1.28); ausente en respuestas viejas. */
  imageDate?: string | null;
  noBoundary?: boolean;
  noApiKey?: boolean;
}

export interface Project {
  projectId: string;
  tenantId: string;
  ranchoId: string;
  name: string;
  season?: string;
  startDate: string;
  endDate?: string;
  currency: string;
  status: ProjectStatus;
}

// ── Módulos ─────────────────────────────────────────────────────────────────

export const fetchModules = async (
  params: { loteId?: string; projectId?: string; date?: string; ranchoId?: string | null } = {},
): Promise<FarmModule[]> =>
  (await apiClient.get('/api/modules', { params: { ...params, ranchoId: params.ranchoId || undefined } })).data.items ?? [];

export const fetchModule = async (moduleId: string): Promise<FarmModule> =>
  (await apiClient.get(`/api/modules/${moduleId}`)).data;

export const createModule = async (input: {
  loteId: string;
  moduleCode: string;
  name?: string;
  cropId?: string;
  valveIds?: string[];
  plantingDate?: string;
  projectId?: string;
  parentModuleId?: string;
  displayColor?: string;
  attendanceZone?: boolean;
}): Promise<FarmModule> =>
  (await apiClient.post('/api/modules', input)).data;

export const updateModule = async (
  moduleId: string,
  patch: Partial<Pick<FarmModule, 'name' | 'cropId' | 'status' | 'plantingDate' | 'projectId' | 'valveIds'>> & { displayColor?: string | null; parentModuleId?: string | null; attendanceZone?: boolean },
): Promise<void> => {
  await apiClient.put(`/api/modules/${moduleId}`, patch);
};

// ── Catálogo de cultivos (mapa avanzado) ──────────────────────────────────────

export const fetchCrops = async (): Promise<Crop[]> =>
  (await apiClient.get('/api/crops')).data.items ?? [];

/** Cobertura FSMA 204 por cultivo (fase E2). Sin lista configurada, todo llega UNKNOWN. */
export type FsmaCoverageStatus = 'COVERED' | 'NOT_COVERED' | 'UNKNOWN';
export const fetchFsmaCoverage = async (): Promise<{ cropId: string; name: string; status: FsmaCoverageStatus }[]> =>
  (await apiClient.get('/api/traceability/fsma/coverage')).data.items ?? [];

export const createCrop = async (input: { name: string; colorHex?: string; cropType?: string }): Promise<Crop> =>
  (await apiClient.post('/api/crops', input)).data;

export const updateCrop = async (cropId: string, patch: { name?: string; colorHex?: string; cropType?: string }): Promise<Crop> =>
  (await apiClient.patch(`/api/crops/${cropId}`, patch)).data;

export const deleteCrop = async (cropId: string): Promise<void> => {
  await apiClient.delete(`/api/crops/${cropId}`);
};

// ── NDVI por módulo (mapa avanzado) ────────────────────────────────────────────

export const fetchModuleCropHistory = async (moduleId: string): Promise<ModuleCropHistoryEntry[]> =>
  (await apiClient.get(`/api/modules/${moduleId}/crop-history`)).data.items ?? [];

export const fetchModulesNdviBatch = async (
  params: { projectId?: string; date?: string } = {},
): Promise<{ results: ModuleNdviResult[]; date: string }> =>
  (await apiClient.get('/api/modules/ndvi-batch', { params })).data;

export const patchModuleBoundary = async (
  moduleId: string,
  boundary: GeoJSONPolygon,
): Promise<{ moduleId: string; areaHa: number }> =>
  (await apiClient.patch(`/api/modules/${moduleId}/boundary`, { boundary })).data;

export interface ZoneCoordinatesCounts { updated: number; alreadySet: number; noBoundary: number }
export interface ZoneCoordinatesResult { lotes: ZoneCoordinatesCounts; modules: ZoneCoordinatesCounts }

/** Calcula lat/lng (centro de la geocerca) de los lotes y zonas de la empresa que no la tienen. */
export const backfillZoneCoordinates = async (): Promise<ZoneCoordinatesResult> =>
  (await apiClient.post('/api/zones/backfill-coordinates')).data;

export const deleteModule = async (moduleId: string): Promise<void> => {
  await apiClient.delete(`/api/modules/${moduleId}`);
};

// ── Proyectos ───────────────────────────────────────────────────────────────

export const fetchProjects = async (): Promise<Project[]> =>
  (await apiClient.get('/api/projects')).data.items ?? [];

export const createProject = async (input: {
  ranchoId: string;
  name: string;
  startDate: string;
  season?: string;
  endDate?: string;
  currency?: string;
}): Promise<Project> =>
  (await apiClient.post('/api/projects', input)).data;
